import fontkit from "@pdf-lib/fontkit";
import boldUrl from "@fontsource/plus-jakarta-sans/files/plus-jakarta-sans-latin-700-normal.woff?url";
import regularUrl from "@fontsource/plus-jakarta-sans/files/plus-jakarta-sans-latin-400-normal.woff?url";
import { PDFDocument, rgb } from "pdf-lib";
import { DEFAULT_LABEL_LAYOUT, labelsPerPage, nlsfbLabel, type AssetDto, type LabelLayout } from "@klantpunt/shared";
import { isOwnToken, qrContent, qrMatrix, qrPath } from "./qr";

const MM = 72 / 25.4;
const GREY = rgb(0.35, 0.33, 0.35);

const bytes = async (url: string) => new Uint8Array(await (await fetch(url)).arrayBuffer());

/** Tekst inkorten tot hij past. */
function fit(text: string, w: number, measure: (s: string) => number) {
  if (measure(text) <= w) return text;
  let s = text;
  while (s.length > 1 && measure(`${s}…`) > w) s = s.slice(0, -1);
  return `${s}…`;
}

/** Positie (linksboven, in pt) van label `n` op een pagina; het raster staat gecentreerd. */
export function labelOrigin(l: LabelLayout, n: number) {
  const gridW = l.cols * l.labelW + (l.cols - 1) * l.gapX;
  const gridH = l.rows * l.labelH + (l.rows - 1) * l.gapY;
  const col = n % l.cols;
  const row = Math.floor(n / l.cols);
  return {
    x: ((l.pageW - gridW) / 2 + col * (l.labelW + l.gapX)) * MM,
    top: (l.pageH - (l.pageH - gridH) / 2 - row * (l.labelH + l.gapY)) * MM,
  };
}

/**
 * QR-labels als PDF in het gekozen formaat (standaard A4, 2 × 6): QR-code (vector), assetcode, naam, categorie,
 * gebouw. Inhoud schaalt mee met de labelmaat. `skip` slaat de eerste labels over (deels gebruikt vel).
 * Een eigen QR-token opent op elke telefoon direct de asset; een gekoppelde sticker (bijv. Assetlog) krijgt een
 * label met dezelfde code, zodat de oude sticker blijft werken. Die vindt de scanner in de app.
 */
export async function buildLabelsPdf(
  assets: AssetDto[],
  buildingName: string,
  layout: LabelLayout = DEFAULT_LABEL_LAYOUT,
  { skip = 0, origin = window.location.origin }: { skip?: number; origin?: string } = {},
) {
  const list = assets.filter((a) => a.qrCode);
  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);
  const [bold, regular] = await Promise.all([
    doc.embedFont(await bytes(boldUrl), { subset: true }),
    doc.embedFont(await bytes(regularUrl), { subset: true }),
  ]);
  doc.setTitle(`QR-labels ${buildingName}`);
  doc.setCreator("Klantpunt (klantpunt.io)");
  const per = labelsPerPage(layout);
  const start = Math.min(Math.max(0, Math.floor(skip)), per - 1);
  const W = layout.labelW * MM;
  const H = layout.labelH * MM;
  let page = doc.addPage([layout.pageW * MM, layout.pageH * MM]);
  list.forEach((a, k) => {
    const n = (k + start) % per;
    if (k > 0 && n === 0) page = doc.addPage([layout.pageW * MM, layout.pageH * MM]);
    const { x: x0, top } = labelOrigin(layout, n);
    if (layout.cutLines)
      page.drawRectangle({
        x: x0,
        y: top - H,
        width: W,
        height: H,
        borderColor: rgb(0.85, 0.85, 0.85),
        borderWidth: 0.4,
      });
    const pad = Math.min(4 * MM, Math.max(1.5 * MM, Math.min(W, H) * 0.08));
    const m = qrMatrix(qrContent(a.qrCode!, origin));
    const qrAt = (qs: number, x: number, y: number) =>
      page.drawSvgPath(qrPath(m), { x, y, scale: qs / (m.size + 8), color: rgb(0, 0, 0) });

    // Liggend label: QR links, tekst rechts (zoals het oorspronkelijke 90 × 42 mm-label).
    const qs = Math.min(H - 2 * pad, W * 0.5);
    const tx = x0 + pad + qs + 3 * MM;
    const tw = x0 + W - pad - tx;
    if (tw >= 18 * MM) {
      qrAt(qs, x0 + pad, top - (H - qs) / 2);
      const f = Math.min(1.6, Math.max(0.45, qs / (34 * MM)));
      const lines: { text: string; size: number; font: typeof bold; color?: ReturnType<typeof rgb> }[] = [
        { text: a.code, size: 13 * f, font: bold },
        { text: a.name, size: 9.5 * f, font: regular },
        { text: nlsfbLabel(a.elementCode), size: 7.5 * f, font: regular, color: GREY },
      ];
      const foot: typeof lines = [
        { text: buildingName, size: 7.5 * f, font: regular, color: GREY },
        {
          text: isOwnToken(a.qrCode!) ? "Scan met je telefoon" : "Scan in de app",
          size: 6.5 * f,
          font: regular,
          color: rgb(0.5, 0.48, 0.5),
        },
      ];
      // Bovenblok van boven naar beneden, voetregels van onderen; wat niet past valt weg.
      let y = top - pad;
      const lowest = top - H + pad;
      const placed: { y: number; l: (typeof lines)[number] }[] = [];
      for (const l of lines) {
        const ny = y - l.size * 1.25;
        if (ny < lowest) break;
        placed.push({ y: ny + l.size * 0.25, l });
        y = ny;
      }
      let fy = top - H + pad;
      for (const l of [...foot].reverse()) {
        if (fy + l.size * 1.2 > y - 1 * MM) break;
        placed.push({ y: fy + l.size * 0.2, l });
        fy += l.size * 1.25;
      }
      for (const { y: ly, l } of placed)
        page.drawText(
          fit(l.text, tw, (s) => l.font.widthOfTextAtSize(s, l.size)),
          { x: tx, y: ly, size: l.size, font: l.font, ...(l.color ? { color: l.color } : {}) },
        );
      return;
    }

    // Klein of staand label: QR gecentreerd, assetcode eronder als er plek is.
    const codeSize = Math.min(11, Math.max(5, W / MM / 3.2));
    const withCode = H - 2 * pad - codeSize * 1.4 >= Math.min(W - 2 * pad, 12 * MM);
    const q2 = Math.min(W - 2 * pad, H - 2 * pad - (withCode ? codeSize * 1.4 : 0));
    const qx = x0 + (W - q2) / 2;
    const qy = top - pad - (withCode ? 0 : (H - 2 * pad - q2) / 2);
    qrAt(q2, qx, qy);
    if (withCode) {
      const t = fit(a.code, W - 2 * pad, (s) => bold.widthOfTextAtSize(s, codeSize));
      page.drawText(t, {
        x: x0 + (W - bold.widthOfTextAtSize(t, codeSize)) / 2,
        y: top - pad - q2 - codeSize * 1.05,
        size: codeSize,
        font: bold,
      });
    }
  });
  return { bytes: await doc.save(), count: list.length };
}

export function downloadPdf(bytes: Uint8Array, filename: string) {
  const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
